import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastSuccess, toastError } from "@/lib/toast";
import {
  toastEntitlementOrError,
} from "@/lib/entitlementToast";
import { API_BASE_URL } from "@/lib/apiBase";
import SprintCadenceFields, {
  cadenceFormValues,
} from "./SprintCadenceFields";

function normalizeLanes(lanesData, teamId) {
  let lanesArray = lanesData;
  if (lanesData && typeof lanesData === "object" && !Array.isArray(lanesData)) {
    lanesArray = lanesData.data || lanesData.lanes || lanesData;
  }
  const tid = parseInt(teamId, 10);
  return Array.isArray(lanesArray)
    ? lanesArray.filter((lane) => !lane.team_id || lane.team_id === tid)
    : [];
}

/**
 * Full-page team settings (team admins / org owner / org admins).
 * Route: /teams/:teamId/settings?org_id=
 */
const TeamSettingsPage = () => {
  const { teamId } = useParams();
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get("org_id");
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { setSelectedOrgId } = useOrgFilter();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [team, setTeam] = useState(null);
  const [kanbanLanes, setKanbanLanes] = useState([]);
  const [isTeamAdminEffective, setIsTeamAdminEffective] = useState(false);
  const [canManageOrgSettings, setCanManageOrgSettings] = useState(false);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    name: "",
    description: "",
    visibility: "restricted",
    searchable: true,
    kanban_enabled: true,
    allowNonMemberIntake: false,
    intake_lane_id: null,
    completed_work_lane_id: null,
    sprint_length_days: 14,
    sprint_start_weekday: 1,
  });

  axios.defaults.withCredentials = true;

  const teamHomePath = orgId
    ? `/teams/${encodeURIComponent(teamId)}?org_id=${encodeURIComponent(orgId)}`
    : `/teams/${encodeURIComponent(teamId)}`;

  useEffect(() => {
    if (orgId) {
      setSelectedOrgId(orgId);
    }
  }, [orgId, setSelectedOrgId]);

  useEffect(() => {
    if (!accessToken || !orgId || !teamId) {
      setLoading(false);
      if (!orgId) setLoadError("Organization ID (org_id) is required");
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const targetId = parseInt(teamId, 10);
        const matchTeam = (list) =>
          (list || []).find((t) => (t.team_id ?? t.teamId) === targetId);

        const [orgRes, meRes, membershipsRes, orgTeamsRes, requestableRes, followsRes] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/org/${orgId}`, {
              headers: { Authorization: `Bearer ${accessToken}` },
            }),
            axios.get(`${API_BASE_URL}/org/${orgId}/me`, {
              headers: { Authorization: `Bearer ${accessToken}` },
            }),
            axios.get(`${API_BASE_URL}/team/memberships`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: { org_id: orgId },
            }),
            axios.get(`${API_BASE_URL}/team/organization`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: { org_id: orgId },
            }),
            axios.get(`${API_BASE_URL}/team/requestable`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: { org_id: orgId },
            }),
            axios.get(`${API_BASE_URL}/team/follows`, {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: { org_id: orgId },
            }),
          ]);

        if (cancelled) return;

        const orgData = orgRes.data;
        const caps = new Set(
          Array.isArray(meRes.data?.capabilities)
            ? meRes.data.capabilities
            : []
        );
        const canManageOrg =
          Boolean(meRes.data?.is_owner) ||
          meRes.data?.role === "admin" ||
          caps.has("org.manage_settings");
        setCanManageOrgSettings(canManageOrg);

        const teamData =
          matchTeam(membershipsRes.data) ||
          matchTeam(orgTeamsRes.data) ||
          matchTeam(requestableRes.data) ||
          matchTeam(followsRes.data);

        if (!teamData) {
          setLoadError("Team not found");
          return;
        }

        const userRes = await axios.get(`${API_BASE_URL}/user/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (cancelled) return;
        const currentUser = userRes.data;
        const isOrgOwner =
          !!currentUser?.user_id &&
          !!orgData?.owned_by &&
          currentUser.user_id === orgData.owned_by;

        let isTeamAdmin = false;
        try {
          const membersRes = await axios.get(
            `${API_BASE_URL}/team/${teamId}/members`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
              params: { org_id: orgId },
            }
          );
          if (cancelled) return;
          const rows = membersRes.data || [];
          const mine = rows.find(
            (item) =>
              item.user?.user_id === currentUser?.user_id ||
              item.user_id === currentUser?.user_id
          );
          isTeamAdmin = mine?.role === "admin";
        } catch {
          /* memberships list may already imply access */
        }

        const adminEffective = isTeamAdmin || isOrgOwner;
        const canOpen = adminEffective || canManageOrg;
        if (!canOpen) {
          navigate(
            `/teams/${encodeURIComponent(teamId)}?org_id=${encodeURIComponent(orgId)}`,
            { replace: true }
          );
          return;
        }

        setIsTeamAdminEffective(adminEffective);
        setTeam(teamData);
        const kanbanOn =
          teamData.kanbanEnabled !== false &&
          teamData.kanban_enabled !== false;
        setSettingsForm({
          name: teamData.name || "",
          description: teamData.description || "",
          visibility: teamData.visibility || "restricted",
          searchable:
            teamData.searchable !== undefined ? teamData.searchable : true,
          kanban_enabled: kanbanOn,
          allowNonMemberIntake: teamData.allowNonMemberIntake || false,
          intake_lane_id: teamData.intake_lane_id ?? null,
          completed_work_lane_id: teamData.completed_work_lane_id ?? null,
          ...cadenceFormValues(teamData),
        });

        if (kanbanOn) {
          try {
            const lanesRes = await axios.get(
              `${API_BASE_URL}/team/${teamId}/kanban-lanes`,
              {
                headers: { Authorization: `Bearer ${accessToken}` },
                params: { org_id: orgId, team_id: targetId },
              }
            );
            if (!cancelled) {
              setKanbanLanes(normalizeLanes(lanesRes.data, teamId));
            }
          } catch {
            if (!cancelled) setKanbanLanes([]);
          }
        }
      } catch (err) {
        console.error("Failed to load team settings:", err);
        if (!cancelled) {
          setLoadError(
            err?.response?.data?.detail ||
              err?.message ||
              "Failed to load team settings"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId, teamId, navigate]);

  const teamKanbanEnabled =
    !!team &&
    team.kanbanEnabled !== false &&
    team.kanban_enabled !== false;

  const handleUpdateSettings = async () => {
    if (!orgId || !team) return;
    setIsUpdatingSettings(true);
    try {
      const {
        sprint_length_days,
        sprint_start_weekday,
        ...settingsWithoutCadence
      } = settingsForm;
      const payload = {
        ...settingsWithoutCadence,
        searchable:
          settingsForm.visibility === "organization"
            ? true
            : settingsForm.searchable,
        allowNonMemberIntake:
          settingsForm.visibility === "restricted" ||
          !settingsForm.kanban_enabled
            ? false
            : settingsForm.allowNonMemberIntake,
        intake_lane_id: settingsForm.kanban_enabled
          ? settingsForm.intake_lane_id
          : null,
        completed_work_lane_id: settingsForm.kanban_enabled
          ? settingsForm.completed_work_lane_id
          : null,
        ...(settingsForm.kanban_enabled
          ? {
              sprint_length_days,
              sprint_start_weekday,
            }
          : {}),
      };
      const response = await axios.patch(
        `${API_BASE_URL}/team/${teamId}/settings?org_id=${orgId}`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const updatedTeam = response.data;
      setTeam(updatedTeam);
      const kanbanOn =
        updatedTeam.kanbanEnabled !== false &&
        updatedTeam.kanban_enabled !== false;
      setSettingsForm({
        name: updatedTeam.name || "",
        description: updatedTeam.description || "",
        visibility: updatedTeam.visibility || "restricted",
        searchable:
          updatedTeam.searchable !== undefined
            ? updatedTeam.searchable
            : true,
        kanban_enabled: kanbanOn,
        allowNonMemberIntake: updatedTeam.allowNonMemberIntake || false,
        intake_lane_id: updatedTeam.intake_lane_id ?? null,
        completed_work_lane_id: updatedTeam.completed_work_lane_id ?? null,
        ...cadenceFormValues(updatedTeam),
      });
      try {
        const lanesRes = await axios.get(
          `${API_BASE_URL}/team/${teamId}/kanban-lanes`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { org_id: orgId, team_id: parseInt(teamId, 10) },
          }
        );
        setKanbanLanes(normalizeLanes(lanesRes.data, teamId));
      } catch (lanesErr) {
        console.error("Failed to refresh lanes after settings save:", lanesErr);
      }
      toastSuccess("Team settings saved");
    } catch (err) {
      console.error("Failed to update settings:", err);
      toastEntitlementOrError(err, "Failed to update settings", {
        canManageSettings: canManageOrgSettings,
        orgId,
      });
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handlePermanentDeleteTeam = async () => {
    if (!canManageOrgSettings || !team?.name || isDeletingTeam) return;
    const expected = String(team.name).trim();
    if (deleteConfirmName.trim() !== expected) {
      toastError("Type the exact team name to confirm deletion");
      return;
    }
    setIsDeletingTeam(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/team/${teamId}/permanent?org_id=${encodeURIComponent(orgId)}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          data: { confirm_name: expected },
        }
      );
      toastSuccess("Team permanently deleted");
      navigate(`/orgs/${orgId}`);
    } catch (err) {
      toastError(err, "Failed to delete team");
    } finally {
      setIsDeletingTeam(false);
    }
  };

  if (!orgId) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">
          Organization ID (org_id) is required
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Loading team settings…
      </div>
    );
  }

  if (loadError || !team) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" onClick={() => navigate(teamHomePath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <p className="text-sm text-destructive">
          {loadError || "Team not found"}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-background text-foreground min-h-screen w-full">
      <header className="flex items-center gap-4 border-b border-border p-4">
        <Button variant="ghost" onClick={() => navigate(teamHomePath)}>
          <ArrowLeft className="h-4 w-4 mr-2 shrink-0" />
          <span className="truncate">Back to {team.name}</span>
        </Button>
      </header>
      <div className="flex-1 p-6 overflow-y-auto max-w-3xl w-full mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Team Settings</CardTitle>
            <CardDescription>
              Manage team configuration and permissions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isTeamAdminEffective ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="team-name">Team Name</Label>
                  <Input
                    id="team-name"
                    value={settingsForm.name}
                    onChange={(e) =>
                      setSettingsForm({ ...settingsForm, name: e.target.value })
                    }
                    placeholder="Enter team name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="team-description">Description</Label>
                  <textarea
                    id="team-description"
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={settingsForm.description}
                    onChange={(e) =>
                      setSettingsForm({
                        ...settingsForm,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter team description"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="visibility"
                        value="organization"
                        checked={settingsForm.visibility === "organization"}
                        onChange={() =>
                          setSettingsForm({
                            ...settingsForm,
                            visibility: "organization",
                            searchable: true,
                          })
                        }
                      />
                      <span>Organization-wide</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="visibility"
                        value="restricted"
                        checked={settingsForm.visibility === "restricted"}
                        onChange={() =>
                          setSettingsForm({
                            ...settingsForm,
                            visibility: "restricted",
                            allowNonMemberIntake: false,
                          })
                        }
                      />
                      <span>Restricted</span>
                    </label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Organization-wide teams are visible to all organization
                    members. Restricted teams require an invitation.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="searchable"
                      checked={
                        settingsForm.visibility === "organization"
                          ? true
                          : settingsForm.searchable
                      }
                      disabled={settingsForm.visibility === "organization"}
                      onChange={(e) =>
                        setSettingsForm({
                          ...settingsForm,
                          searchable: e.target.checked,
                        })
                      }
                      className="h-4 w-4 rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <Label
                      htmlFor="searchable"
                      className={
                        settingsForm.visibility === "organization"
                          ? "text-muted-foreground"
                          : undefined
                      }
                    >
                      Searchable
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {settingsForm.visibility === "organization"
                      ? "Organization-wide teams are always searchable."
                      : "When enabled, this team appears in search results and can receive join requests."}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="kanban-enabled"
                      checked={settingsForm.kanban_enabled !== false}
                      onChange={(e) =>
                        setSettingsForm({
                          ...settingsForm,
                          kanban_enabled: e.target.checked,
                          ...(e.target.checked
                            ? {}
                            : {
                                allowNonMemberIntake: false,
                                intake_lane_id: null,
                                completed_work_lane_id: null,
                              }),
                        })
                      }
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="kanban-enabled">Kanban board</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    When off, the Kanban tab is hidden and cards leave org/team
                    feeds. Existing lanes and cards are kept and will reappear
                    if you turn the board back on.
                  </p>
                  {teamKanbanEnabled &&
                    settingsForm.kanban_enabled === false && (
                      <p className="text-sm text-amber-600 dark:text-amber-500">
                        Saving will hide the board and cards from feeds until
                        re-enabled. Nothing is deleted.
                      </p>
                    )}
                </div>

                {settingsForm.kanban_enabled !== false && (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="allow-non-member-intake"
                          checked={
                            settingsForm.visibility === "restricted"
                              ? false
                              : settingsForm.allowNonMemberIntake
                          }
                          disabled={settingsForm.visibility === "restricted"}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              allowNonMemberIntake: e.target.checked,
                            })
                          }
                          className="h-4 w-4 rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        <Label
                          htmlFor="allow-non-member-intake"
                          className={
                            settingsForm.visibility === "restricted"
                              ? "text-muted-foreground"
                              : undefined
                          }
                        >
                          Allow non-members to submit intake cards
                        </Label>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {settingsForm.visibility === "restricted"
                          ? "Restricted teams cannot accept non-member intake. Only members can create cards."
                          : "When enabled, organization members who can view this team may submit kanban cards to the intake lane. Only team members can create feed posts."}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="intake-lane">Intake lane</Label>
                      <select
                        id="intake-lane"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        value={settingsForm.intake_lane_id ?? ""}
                        onChange={(e) =>
                          setSettingsForm({
                            ...settingsForm,
                            intake_lane_id: e.target.value
                              ? parseInt(e.target.value, 10)
                              : null,
                          })
                        }
                      >
                        <option value="">None</option>
                        {[...kanbanLanes]
                          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                          .map((lane) => (
                            <option key={lane.lane_id} value={lane.lane_id}>
                              {lane.name}
                            </option>
                          ))}
                      </select>
                      <p className="text-sm text-muted-foreground">
                        Non-member intake cards always go to this lane. Selecting
                        a lane marks it as hidden from the org feed.
                      </p>
                      {settingsForm.visibility !== "restricted" &&
                        settingsForm.allowNonMemberIntake &&
                        !settingsForm.intake_lane_id && (
                          <p className="text-sm text-amber-600 dark:text-amber-500">
                            Non-member intake is enabled, but card creation will
                            fail until an intake lane is set.
                          </p>
                        )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="completed-work-lane">
                        Completed work lane
                      </Label>
                      <select
                        id="completed-work-lane"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        value={settingsForm.completed_work_lane_id ?? ""}
                        onChange={(e) =>
                          setSettingsForm({
                            ...settingsForm,
                            completed_work_lane_id: e.target.value
                              ? parseInt(e.target.value, 10)
                              : null,
                          })
                        }
                      >
                        <option value="">None</option>
                        {[...kanbanLanes]
                          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                          .map((lane) => (
                            <option key={lane.lane_id} value={lane.lane_id}>
                              {lane.name}
                            </option>
                          ))}
                      </select>
                      <p className="text-sm text-muted-foreground">
                        Cards in this lane count as finished work in the org feed
                        (brief ship signal, then down-ranked).
                      </p>
                    </div>

                    <SprintCadenceFields
                      idPrefix="settings-sprint-cadence"
                      lengthDays={settingsForm.sprint_length_days}
                      startWeekday={settingsForm.sprint_start_weekday}
                      disabled={isUpdatingSettings}
                      onChange={({ lengthDays, startWeekday }) =>
                        setSettingsForm({
                          ...settingsForm,
                          sprint_length_days: lengthDays,
                          sprint_start_weekday: startWeekday,
                        })
                      }
                    />
                  </>
                )}

                <Button
                  onClick={handleUpdateSettings}
                  disabled={isUpdatingSettings}
                  className="w-full"
                >
                  {isUpdatingSettings ? "Saving..." : "Save Settings"}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Team configuration is managed by team admins. As a workspace
                admin you can permanently delete this team below.
              </p>
            )}

            {canManageOrgSettings && (
              <div className="space-y-4 border-t border-destructive/30 pt-6">
                <div>
                  <h3 className="text-base font-semibold text-destructive">
                    Delete team permanently
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    This cannot be undone. All posts, cards, channels, files, and
                    memberships for this team will be removed forever.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delete-team-confirm">
                    Type{" "}
                    <span className="font-medium text-foreground">
                      {team?.name || "the team name"}
                    </span>{" "}
                    to confirm
                  </Label>
                  <Input
                    id="delete-team-confirm"
                    value={deleteConfirmName}
                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                    placeholder={team?.name || "Team name"}
                    autoComplete="off"
                    disabled={isDeletingTeam}
                  />
                </div>
                <Button
                  variant="destructive"
                  className="w-full"
                  disabled={
                    isDeletingTeam ||
                    !team?.name ||
                    deleteConfirmName.trim() !== String(team.name).trim()
                  }
                  onClick={handlePermanentDeleteTeam}
                >
                  {isDeletingTeam ? "Deleting…" : "Delete team permanently"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeamSettingsPage;
